import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { ProtectedEmail } from '../utils/emailProtection';
import ResponsiveImage from './ResponsiveImage';
import './Footer.css';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t, localizedPath } = useLanguage();

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-main">
          <div className="footer-brand">
            <div className="footer-logo">
              <ResponsiveImage
                src="/images/logo.webp"
                alt="Radix Technologies Logo"
                width="120"
                height="30"
                style={{ objectFit: 'contain' }}
              />
            </div>
            <p className="footer-tagline">
              {t('footer.description')}
            </p>
          </div>

          <div className="footer-links-grid">
            <div className="footer-section">
              <h3 className="footer-title">{t('footer.services')}</h3>
              <ul className="footer-links">
                <li><Link to={localizedPath('/services#devops-automation')}>{t('footer.devopsAutomation')}</Link></li>
                <li><Link to={localizedPath('/services#cloud-migration')}>{t('footer.cloudMigration')}</Link></li>
                <li><Link to={localizedPath('/services#managed-services')}>{t('footer.managedServices')}</Link></li>
                <li><Link to={localizedPath('/services#ai-solutions')}>{t('footer.technologyAdvisory')}</Link></li>
                <li><Link to={localizedPath('/services#mobile-development')}>{t('footer.mobileDevelopment')}</Link></li>
                <li><Link to={localizedPath('/services#quality-assurance')}>{t('footer.qualityAssurance')}</Link></li>
              </ul>
            </div>

            <div className="footer-section">
              <h3 className="footer-title">{t('footer.quickLinks')}</h3>
              <ul className="footer-links">
                <li><Link to={localizedPath('/about')}>{t('footer.aboutUs')}</Link></li>
                <li><Link to={localizedPath('/portfolio')}>{t('footer.portfolio')}</Link></li>
                <li><Link to={localizedPath('/blog')}>{t('footer.blog')}</Link></li>
                <li><Link to={localizedPath('/careers')}>{t('footer.careers')}</Link></li>
                <li><Link to={localizedPath('/plans')}>{t('footer.pricing')}</Link></li>
                <li><Link to={localizedPath('/contact')}>{t('footer.contactLink')}</Link></li>
                <li><Link to={localizedPath('/about')}>{t('footer.ourTeam')}</Link></li>
              </ul>
            </div>

            <div className="footer-section">
              <h3 className="footer-title">{t('footer.contact')}</h3>
              <div className="contact-info">
                <div className="contact-item">
                  <svg className="contact-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                  </svg>
                  <ProtectedEmail email="info@radixtechs.com" displayText="info@radixtechs.com" />
                </div>
                <div className="contact-item">
                  <svg className="contact-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                  </svg>
                  <a href="tel:+972923377772">+972-92-337-7772</a>
                </div>
                <div className="contact-item">
                  <svg className="contact-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                  </svg>
                  <span>{t('footer.location')}</span>
                </div>
                <div className="contact-item">
                  <svg className="contact-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
                    <path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
                  </svg>
                  <span>{t('footer.workingHours')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <div className="footer-bottom-content">
            <div className="footer-legal">
              <p className="footer-copyright">
                © {currentYear} Radix Technologies. {t('footer.allRightsReserved')}
              </p>
            </div>

            <div className="social-links">
              <span className="social-title">{t('footer.followUs')}</span>
              <div className="social-icons">
                <a href="https://linkedin.com/company/radix-techs" className="social-link focus-color-ring" aria-label="LinkedIn">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
                <a href="https://facebook.com/RadixTechs" className="social-link focus-color-ring" aria-label="Facebook">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
